import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { VendorItem, VendorListResponse } from '../vendor.config';
import { VendorControllerService } from '../../../../../api/vendor-service/api/vendorController.service';
import { VendorSkuControllerService } from '../../../../../api/vendor-service/api/vendorSkuController.service';
import { PageInfoVendorVO } from '@app/api/vendor-service/model/pageInfoVendorVO';
import { VendorVO } from '@app/api/vendor-service/model/vendorVO';
import { VendorDTO } from '@app/api/vendor-service/model/vendorDTO';
import { Vendor } from '@app/api/vendor-service/model/vendor';
import { VendorSkuBulkCreateDTO } from '@app/api/vendor-service/model/vendorSkuBulkCreateDTO';
import { VendorSkuBulkCreateResult } from '@app/api/vendor-service/model/vendorSkuBulkCreateResult';
import { normalizeVietnamPhoneNumber } from '@shared/utils/phone-validator.util';

export interface SearchFormData {
  code?: string;
  name?: string;
  phone?: string;
  status?: string;
}

export interface VendorSearchParams {
  code?: string;
  name?: string;
  phone?: string;
  status?:
    | 'DRAFT'
    | 'REVIEWING'
    | 'REJECTED'
    | 'APPROVED'
    | 'ACTIVE'
    | 'INACTIVE';
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class VendorService {
  private vendorController = inject(VendorControllerService);
  private vendorSkuController = inject(VendorSkuControllerService);

  // Get Vendors list with pagination and search
  getVendors(params: any): Observable<VendorListResponse> {
    const {
      code,
      name,
      phone,
      status,
      page = 0,
      size = 20,
      sortBy = 'creationDate',
      ascending = false,
      skuCodes,
    } = params;

    // Use API status directly - no mapping needed
    // Vendor workflow: DRAFT → REVIEWING → (REJECTED/APPROVED) → ACTIVE → INACTIVE
    const apiStatus = status as
      | 'DRAFT'
      | 'REVIEWING'
      | 'REJECTED'
      | 'APPROVED'
      | 'ACTIVE'
      | 'INACTIVE'
      | undefined;

    // Call the page API
    return this.vendorController
      .page(
        undefined, // id
        code?.trim(), // vendorCode
        name?.trim(), // vendorName
        undefined, // vendorEmail
        phone ? normalizeVietnamPhoneNumber(phone.trim()) : undefined, // vendorPhone (normalize missing leading "0")
        undefined, // vendorAddress
        false, // deleteFlag - only get non-deleted vendors
        undefined, // country
        undefined, // taxNumber
        undefined, // vendorType
        undefined, // billingCurrency
        undefined, // paymentTerm
        undefined, // paymentMethod
        undefined, // bankAccountName
        undefined, // bankAccountNumber
        undefined, // bankName
        undefined, // swiftCode
        undefined, // category
        undefined, // riskLevel
        skuCodes, // skuCodes
        apiStatus, // status
        page, // page
        size, // size
        sortBy, // sortBy
        ascending, // ascending
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        map((response: PageInfoVendorVO) => {
          const mappedData = this.mapApiResponseToLocalData(response);
          return {
            data: mappedData.data || [],
            totalRecords: mappedData.totalRecords || 0,
            loading: false,
          };
        }),
        catchError((error) => {
          console.error('Error fetching Vendors:', error);
          throw error;
        })
      );
  }

  // Get Vendor by ID
  // Note: id can be numeric id or vendorCode (string)
  getVendorById(id: string): Observable<VendorItem | null> {
    // Check if id is numeric (actual id) or string (vendorCode)
    const numericId = Number(id);
    const isNumericId = !isNaN(numericId) && isFinite(numericId);

    if (isNumericId) {
      // Use getById for numeric id - returns VendorDetailsVO which has bankAccounts
      return this.vendorController
        .getById(numericId, 'body', false, {
          httpHeaderAccept: 'application/json' as any,
        })
        .pipe(
          map((response: any) => {
            if (response) {
              return this.mapApiItemToLocalItem(response, numericId);
            }
            return null;
          }),
          catchError((error) => {
            console.error('Error fetching Vendor by ID:', error);
            return of(null);
          })
        );
    } else {
      // Use getByCode for vendorCode
      return this.vendorController
        .getByCode(id, 'body', false, {
          httpHeaderAccept: 'application/json' as any,
        })
        .pipe(
          map((response: VendorVO) => {
            if (response) {
              // Try to extract numeric ID from response (it might be in the response object)
              const responseAny = response as any;
              const numericId = responseAny.id ? Number(responseAny.id) : null;
              return this.mapApiItemToLocalItem(
                response,
                numericId || undefined
              );
            }
            return null;
          }),
          catchError((error) => {
            console.error('Error fetching Vendor by Code:', error);
            return of(null);
          })
        );
    }
  }

  // Delete Vendor
  // Note: Requires numeric id. If vendorCode is passed, it will fail.
  // This is a limitation - ideally the page API should return id in VendorVO
  deleteVendor(id: string): Observable<string> {
    const numericId = Number(id);
    if (isNaN(numericId) || !isFinite(numericId)) {
      throw new Error(
        'Invalid vendor id. Delete operation requires numeric id, but vendorCode was provided. This is a limitation of the current API.'
      );
    }

    return this.vendorController.deleteVendor(numericId).pipe(
      map(() => 'Vendor deleted successfully'),
      catchError((error) => {
        console.error('Error deleting Vendor:', error);
        throw error;
      })
    );
  }

  // Create Vendor
  createVendor(data: VendorDTO): Observable<Vendor> {
    return this.vendorController
      .addVendor(data, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: Vendor) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error creating Vendor:', error);
          throw error;
        })
      );
  }

  /**
   * Bulk create vendors (used by vendor_creation_template.csv import).
   * Backend: POST /v1/vendor/list
   */
  createVendorsBulk(data: VendorDTO[]): Observable<Vendor[]> {
    if (!data || data.length === 0) {
      return of([]);
    }

    return this.vendorController
      .addListVendor(data, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: Vendor[]) => response),
        catchError((error) => {
          console.error('Error creating vendors bulk:', error);
          throw error;
        }),
      );
  }

  // Update Vendor
  // Note: Requires numeric id. If vendorCode is passed, it will fail.
  // This is a limitation - ideally the page API should return id in VendorVO
  updateVendor(id: string, data: VendorDTO): Observable<Vendor> {
    const numericId = Number(id);
    if (isNaN(numericId) || !isFinite(numericId)) {
      throw new Error(
        'Invalid vendor id. Update operation requires numeric id, but vendorCode was provided. This is a limitation of the current API.'
      );
    }

    return this.vendorController
      .updateVendor(numericId, data, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: Vendor) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error updating Vendor:', error);
          throw error;
        })
      );
  }

  // Change Vendor Status
  changeStatus(
    id: number,
    status:
      | 'DRAFT'
      | 'REVIEWING'
      | 'REJECTED'
      | 'PENDING'
      | 'APPROVED'
      | 'ACTIVE'
      | 'INACTIVE'
  ): Observable<VendorVO> {
    return this.vendorController
      .changeStatus(id, status, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: VendorVO) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error changing vendor status:', error);
          throw error;
        })
      );
  }

  // Validate Vendor Tax ID
  validateVendorTaxId(taxId: string): Observable<any> {
    return this.vendorController
      .validateVendorTaxId(taxId, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error validating vendor tax ID:', error);
          throw error;
        })
      );
  }

  // Map API response to local data structure
  private mapApiResponseToLocalData(
    apiResponse: PageInfoVendorVO
  ): VendorListResponse {
    const mappedData = (apiResponse.pageContent || []).map((item: VendorVO) =>
      this.mapApiItemToLocalItem(item)
    );

    return {
      data: mappedData,
      totalRecords: apiResponse.totalElements || 0,
      loading: false,
    };
  }

  // Map API item to local item structure
  // Note: apiItem can be VendorVO (from list) or VendorDetailsVO (from getById/getByCode)
  private mapApiItemToLocalItem(
    apiItem: VendorVO | any,
    id?: number
  ): VendorItem {
    // Use API status directly - no mapping needed
    const mappedStatus = apiItem.status || 'DRAFT';

    // Extract numeric ID from API response (it exists in actual response but not in VendorVO type)
    // Using type assertion to access the property that exists in the actual API response
    const apiItemAny = apiItem as any;
    const numericIdFromResponse = apiItemAny.id ? Number(apiItemAny.id) : null;
    const creationDate = apiItemAny.creationDate || '';

    // Priority: 1) provided id parameter, 2) id from API response, 3) vendorCode as fallback
    // Always prefer numeric ID for navigation to edit/view pages
    let vendorId: string;
    if (id) {
      vendorId = id.toString();
    } else if (
      numericIdFromResponse !== null &&
      !isNaN(numericIdFromResponse)
    ) {
      vendorId = numericIdFromResponse.toString();
    } else {
      // Fallback to vendorCode only if numeric ID is not available
      vendorId = apiItem.vendorCode || '';
    }

    // Extract bankAccounts from VendorDetailsVO (when available)
    const bankAccounts = apiItemAny.bankAccounts || null;

    const mappedItem: VendorItem & { bankAccounts?: any } = {
      id: vendorId,
      code: apiItem.vendorCode || '',
      name: apiItem.vendorName || '',
      phone: apiItem.vendorPhone || '',
      email: apiItem.vendorEmail || '',
      address: apiItem.vendorAddress || '',
      vendorType: apiItem.vendorType || '',
      category: apiItem.category || '',
      status: mappedStatus,
      creationDate: creationDate,
      // Additional fields for edit form
      description: apiItem.description || '',
      country: apiItem.country || '',
      taxNumber: apiItem.taxNumber || '',
      billingCurrency: apiItem.billingCurrency || '',
      paymentTerm: apiItem.paymentTerm || '',
      paymentMethod: apiItem.paymentMethod || '',
      bankAccountName: apiItem.bankAccountName || '',
      bankAccountNumber: apiItem.bankAccountNumber || '',
      bankName: apiItem.bankName || '',
      swiftCode: apiItem.swiftCode || '',
      riskLevel: apiItem.riskLevel || '',
      vendorPostingGroup: apiItemAny.vendorPostingGroup || '',
      generalBusinessPostingGroup: apiItemAny.generalBusinessPostingGroup || '',
      vatBusinessPostingGroup: apiItemAny.vatBusinessPostingGroup || '',
      invoiceDiscCode: apiItemAny.invoiceDiscCode || '',
    };

    // Preserve bankAccounts if present (from VendorDetailsVO)
    if (bankAccounts) {
      (mappedItem as any).bankAccounts = bankAccounts;
    }

    return mappedItem as VendorItem;
  }

  /**
   * Bulk create VendorSKU mappings (used by vendor_product_mapping_template.csv import).
   * Backend: POST /v1/vendor/sku/bulk
   */
  createVendorSkuBulk(payload: VendorSkuBulkCreateDTO[]): Observable<VendorSkuBulkCreateResult> {
    return this.vendorSkuController
      .addVendorSkuBulk(payload, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        catchError((error) => {
          console.error('Error creating vendor SKU bulk:', error);
          throw error;
        }),
      );
  }
}
